import { saveAs } from 'file-saver';
import { supabase } from '../OS/integrations/supabase/client';
import type { Database } from '../OS/integrations/supabase/types';
import type {
  ShortcutApplyAction,
  ShortcutDownloadedFile,
  ShortcutRemoteListResponse,
  ShortcutRemoteRun,
  ShortcutRunRecord,
  ShortcutSourceType,
  ShortcutSubmitResponse,
  ShortcutUploadResponse,
  ShortcutVerifyResponse,
} from '../OS/components/apps/shortcut/types';
import {
  buildMapleSheetCsvExport,
  buildPreviewCache,
  diffCurrentSheetWithResult,
  parseShortcutResultForPreview,
} from '../OS/components/apps/shortcut/utils';

const SHORTCUT_PROXY_FUNCTION = 'shortcut-proxy';
const SHORTCUT_PROXY_URL = 'https://service.canxp.ai/functions/v1/shortcut-proxy';
const ALLOWED_FILE_EXTENSIONS = ['csv', 'xlsx', 'xlsm'] as const;
const MAX_FILE_BYTES = 100 * 1024 * 1024;

type ShortcutRunRow = Database['public']['Tables']['shortcut_runs']['Row'];
type ShortcutRunInsert = Database['public']['Tables']['shortcut_runs']['Insert'];
type ShortcutRunUpdate = Database['public']['Tables']['shortcut_runs']['Update'];

const shortcutRunsTable = () => supabase.from('shortcut_runs') as any;

function normalizeErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }
  return typeof error === 'string' ? error : 'Unexpected Shortcut error';
}

function getExtension(fileName: string) {
  return fileName.toLowerCase().split('.').pop() || '';
}

function ensureAllowedFile(file: File) {
  const extension = getExtension(file.name);
  if (!ALLOWED_FILE_EXTENSIONS.includes(extension as (typeof ALLOWED_FILE_EXTENSIONS)[number])) {
    throw new Error('Shortcut supports CSV, XLSX, and XLSM files only.');
  }

  if (file.size > MAX_FILE_BYTES) {
    throw new Error('Shortcut file uploads are limited to 100MB.');
  }
}

async function getAccessToken() {
  const { data } = await supabase.auth.getSession();
  const accessToken = data.session?.access_token;
  if (!accessToken) {
    throw new Error('You must be signed in to use Shortcut.');
  }
  return accessToken;
}

async function invokeShortcut<T>(action: string, payload: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.functions.invoke(SHORTCUT_PROXY_FUNCTION, {
    body: {
      action,
      ...payload,
    },
  });

  if (error) {
    throw new Error(error.message || `Shortcut ${action} request failed.`);
  }

  if (data?.success === false) {
    throw new Error(data.message || data.error || `Shortcut ${action} request failed.`);
  }

  return data as T;
}

async function fetchShortcutFile(action: 'download' | 'artifact', searchParams: URLSearchParams): Promise<ShortcutDownloadedFile> {
  const accessToken = await getAccessToken();
  const response = await fetch(`${SHORTCUT_PROXY_URL}?action=${action}&${searchParams.toString()}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const errorBody = await response.json();
      throw new Error(errorBody.message || errorBody.error || 'Shortcut download failed.');
    }
    throw new Error(await response.text() || 'Shortcut download failed.');
  }

  const blob = await response.blob();
  const fileName = decodeURIComponent(response.headers.get('x-shortcut-file-name') || 'shortcut-result.xlsx');
  const contentType = response.headers.get('content-type') || 'application/octet-stream';

  return {
    blob,
    fileName,
    contentType,
    sizeBytes: blob.size,
  };
}

function mapShortcutRunRow(row: ShortcutRunRow): ShortcutRunRecord {
  return {
    ...row,
    status: row.status as ShortcutRunRecord['status'],
    mode: row.mode as ShortcutRunRecord['mode'],
    applied_action: row.applied_action as ShortcutRunRecord['applied_action'],
    source_type: row.source_type as ShortcutSourceType,
    skills: Array.isArray(row.skills) ? (row.skills as string[]) : [],
    artifacts_json: Array.isArray(row.artifacts_json) ? (row.artifacts_json as string[]) : [],
    preview_cache: row.preview_cache ? (row.preview_cache as ShortcutRunRecord['preview_cache']) : null,
  };
}

export function saveShortcutFile(file: ShortcutDownloadedFile) {
  saveAs(file.blob, file.fileName);
}

export async function verifyShortcutConnection() {
  return invokeShortcut<ShortcutVerifyResponse>('verify');
}

export async function listShortcutRemoteRuns(limit = 50, offset = 0) {
  return invokeShortcut<ShortcutRemoteListResponse>('list', { limit, offset });
}

export async function getShortcutRun(runId: string) {
  return invokeShortcut<ShortcutRemoteRun>('status', { runId });
}

export async function submitShortcutJob(payload: {
  prompt: string;
  initFile?: string;
  contextFiles?: string[];
  timeoutMs?: number;
  mode?: 'action' | 'ask';
  skills?: string[];
}) {
  return invokeShortcut<ShortcutSubmitResponse>('submit', payload);
}

export async function uploadShortcutFile(file: File) {
  ensureAllowedFile(file);
  const accessToken = await getAccessToken();
  const formData = new FormData();
  formData.append('action', 'upload');
  formData.append('file', file);

  const response = await fetch(SHORTCUT_PROXY_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });

  const data = await response.json();
  if (!response.ok || data?.success === false) {
    throw new Error(data?.message || data?.error || 'Shortcut upload failed.');
  }

  return data as ShortcutUploadResponse;
}

export async function downloadShortcutResult(runId: string) {
  return fetchShortcutFile('download', new URLSearchParams({ runId }));
}

export async function downloadShortcutArtifact(runId: string, filename: string) {
  return fetchShortcutFile('artifact', new URLSearchParams({ runId, filename }));
}

export async function parseShortcutReviewData(params: {
  runId: string;
  currentTable?: (string | number)[][];
}) {
  const file = await downloadShortcutResult(params.runId);
  const preview = await parseShortcutResultForPreview(file);
  const diff = preview && params.currentTable
    ? diffCurrentSheetWithResult(params.currentTable, preview.table)
    : null;

  return {
    file,
    preview,
    diff,
  };
}

export function buildShortcutPreviewCacheFromPreview(preview: Awaited<ReturnType<typeof parseShortcutResultForPreview>>) {
  return preview ? buildPreviewCache(preview) : null;
}

export { buildMapleSheetCsvExport, parseShortcutResultForPreview, diffCurrentSheetWithResult };

export async function createShortcutRunRecord(payload: ShortcutRunInsert) {
  const { data, error } = await shortcutRunsTable()
    .insert(payload)
    .select('*')
    .single();

  if (error) {
    throw new Error(error.message || 'Failed to save Shortcut run history.');
  }

  return mapShortcutRunRow(data as ShortcutRunRow);
}

export async function updateShortcutRunRecord(id: string, payload: ShortcutRunUpdate) {
  const { data, error } = await shortcutRunsTable()
    .update(payload)
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    throw new Error(error.message || 'Failed to update Shortcut run history.');
  }

  return mapShortcutRunRow(data as ShortcutRunRow);
}

export async function updateShortcutRunRecordByRemoteId(shortcutRunId: string, payload: ShortcutRunUpdate) {
  const { data, error } = await shortcutRunsTable()
    .update(payload)
    .eq('shortcut_run_id', shortcutRunId)
    .select('*')
    .single();

  if (error) {
    throw new Error(error.message || 'Failed to update Shortcut run history.');
  }

  return mapShortcutRunRow(data as ShortcutRunRow);
}

export async function listShortcutRunHistory() {
  const { data, error } = await shortcutRunsTable()
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(error.message || 'Failed to load Shortcut history.');
  }

  return (data as ShortcutRunRow[]).map(mapShortcutRunRow);
}

export async function markShortcutRunApplied(id: string, appliedAction: ShortcutApplyAction) {
  return updateShortcutRunRecord(id, {
    applied_action: appliedAction,
  });
}

export async function syncShortcutRunStatus(run: ShortcutRunRecord, currentTable?: (string | number)[][]) {
  try {
    const remoteRun = await getShortcutRun(run.shortcut_run_id);
    if (remoteRun.status === 'completed') {
      const reviewData = await parseShortcutReviewData({
        runId: run.shortcut_run_id,
        currentTable,
      });

      const updatedRun = await updateShortcutRunRecord(run.id, {
        status: remoteRun.status,
        result_download_available: true,
        result_file_name: reviewData.file.fileName,
        result_content_type: reviewData.file.contentType,
        result_size_bytes: reviewData.file.sizeBytes,
        remote_summary: remoteRun.summary || null,
        artifacts_json: remoteRun.artifacts,
        preview_cache: buildShortcutPreviewCacheFromPreview(reviewData.preview),
        completed_at: new Date().toISOString(),
        error_message: null,
      });

      return {
        run: updatedRun,
        reviewData,
      };
    }

    if (remoteRun.status === 'failed' || remoteRun.status === 'error') {
      const updatedRun = await updateShortcutRunRecord(run.id, {
        status: remoteRun.status,
        error_message: remoteRun.error,
        completed_at: new Date().toISOString(),
      });

      return {
        run: updatedRun,
        reviewData: null,
      };
    }

    const updatedRun = await updateShortcutRunRecord(run.id, {
      status: remoteRun.status,
    });

    return {
      run: updatedRun,
      reviewData: null,
    };
  } catch (error) {
    const updatedRun = await updateShortcutRunRecord(run.id, {
      status: 'error',
      error_message: normalizeErrorMessage(error),
      completed_at: new Date().toISOString(),
    });

    return {
      run: updatedRun,
      reviewData: null,
    };
  }
}

export function buildShortcutPrompt(params: {
  userPrompt: string;
  selectedRange?: string | null;
  activeCell: string;
  sourceLabel: string;
}) {
  const context = params.selectedRange
    ? `Focus primarily on the selected range ${params.selectedRange}.`
    : `Use the active cell ${params.activeCell} as the focal point if the sheet has multiple regions.`;

  return [
    params.userPrompt.trim(),
    '',
    `MapleSheets context: ${params.sourceLabel}.`,
    context,
    'Return spreadsheet-ready results that can be reviewed before applying changes.',
  ].join('\n');
}

export function getShortcutSourceTypeFromName(fileName: string, currentSheet = false): ShortcutSourceType {
  if (currentSheet) {
    return 'msheet';
  }

  const extension = getExtension(fileName);
  if (extension === 'xlsx') {
    return 'xlsx';
  }
  if (extension === 'xlsm') {
    return 'xlsm';
  }
  return 'csv';
}
